import { useSearchParams } from 'react-router-dom';

import DashboardLayout from '@/components/layout/DashboardLayout';
import { clubSidebar, organizerSidebar } from '@/config/sidebarConfig';
import { getSiteCmsAccessApi } from '@/lib/api/siteCmsApi';
import { useAuthStore } from '@/stores/auth-store';
import { Role } from '@shared/enums/role.enum';
import type { SiteCmsOwnerType } from '@shared/types/entities';
import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight, Globe2, Loader2, LockKeyhole } from 'lucide-react';

import SiteCmsEditor from './SiteCmsEditor';
import { parseSiteOwnerId, resolveSiteMembership } from './resolve-site-membership';

const MARKETING_URL = `${(import.meta.env.VITE_URL_FRONT || 'http://localhost:5232').replace(/\/$/, '')}/fonctionnalites/site-web`;

export default function SiteCmsLanding() {
  const user = useAuthStore(state => state.user);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const [searchParams, setSearchParams] = useSearchParams();
  const organizer = user?.role === Role.ORGANIZATION_ADMIN;
  const ownerType: SiteCmsOwnerType = organizer ? 'ORGANIZATION' : 'CLUB';
  const memberships = user?.organizationsAdmin ?? [];
  const requestedId = parseSiteOwnerId(searchParams.get('organizationId'));
  const membership = organizer ? resolveSiteMembership(memberships, requestedId) : null;
  const ownerId = organizer ? membership?.organizationId : user?.clubId;
  const sidebarConfig = organizer ? organizerSidebar : clubSidebar;

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['site-cms', 'access', user?.id, ownerType, ownerId],
    queryFn: () => getSiteCmsAccessApi(ownerType, ownerId as number),
    enabled: Boolean(isAuthenticated && ownerId),
    retry: false,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });

  const denied = [401, 403, 404].includes((error as { status?: number } | null)?.status ?? 0);
  const preservedAccess = data?.access === 'ACTIVE' && !denied;
  const showPicker = organizer && memberships.length > 1 && !membership;
  return (
    <DashboardLayout sidebarConfig={sidebarConfig}>
      <div className={data?.access === 'ACTIVE' ? 'min-w-0 px-4 py-4 lg:px-7' : 'mx-auto max-w-4xl p-6 md:p-10'}>
        {data?.access !== 'ACTIVE' && (
          <div className='mb-8 flex items-center gap-3'>
            <div className='rounded-xl bg-blue-50 p-3 text-blue-700'>
              <Globe2 className='h-7 w-7' />
            </div>
            <div>
              <h1 className='text-2xl font-bold text-slate-900'>Mon site</h1>
              <p className='text-sm text-slate-500'>Votre futur site vitrine, géré depuis BoulesConnect.</p>
            </div>
          </div>
        )}

        {showPicker ? (
          <section className='rounded-2xl border border-slate-200 bg-white p-6'>
            <h2 className='text-lg font-bold text-slate-900'>Choisissez votre organisation</h2>
            <p className='mt-1 text-sm text-slate-600'>L’offre Site est attribuée séparément à chaque organisation.</p>
            <div className='mt-5 space-y-2'>
              {memberships.map(item => (
                <button
                  type='button'
                  key={item.organizationId}
                  onClick={() => setSearchParams({ organizationId: String(item.organizationId) })}
                  className='w-full rounded-xl border border-slate-200 px-4 py-3 text-left text-sm font-semibold text-slate-800 hover:border-blue-300 hover:bg-blue-50'
                >
                  {item.organization?.name ?? `Organisation #${item.organizationId}`}
                </button>
              ))}
            </div>
          </section>
        ) : !ownerId ? (
          <div role='alert' className='rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800'>
            Aucune structure administrée disponible pour ce compte.
          </div>
        ) : isPending ? (
          <div className='flex items-center gap-3 text-slate-600'>
            <Loader2 className='h-5 w-5 animate-spin' /> Vérification des droits…
          </div>
        ) : isError && !preservedAccess ? (
          <div role='alert' className='rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-800'>
            {denied
              ? 'Votre accès à cette structure n’est plus autorisé.'
              : 'Impossible de vérifier votre accès. Votre brouillon n’a pas été supprimé.'}
            <button type='button' className='ml-3 font-semibold underline' onClick={() => void refetch()}>
              Réessayer
            </button>
          </div>
        ) : data?.access === 'ACTIVE' ? (
          <SiteCmsEditor
            ownerType={ownerType}
            ownerId={ownerId}
            ownerName={data.ownerName}
            accessSuspended={isError}
            onRetryAccess={() => {
              void refetch();
            }}
            onChangeOwner={organizer && memberships.length > 1 ? () => setSearchParams({}) : undefined}
          />
        ) : (
          <section className='rounded-2xl border border-slate-200 bg-white p-8'>
            <LockKeyhole className='mb-4 h-10 w-10 text-slate-500' />
            <h2 className='text-xl font-bold text-slate-900'>Créez le site web de votre association</h2>
            <p className='mt-3 max-w-2xl text-sm leading-relaxed text-slate-600'>
              Une vitrine personnalisable pour présenter votre association, publier vos pages, documents et actualités,
              organiser vos menus sur trois niveaux et mettre en avant vos concours BoulesConnect.
            </p>
            <div className='mt-6 grid max-w-lg grid-cols-2 gap-3'>
              <div className='rounded-xl border border-slate-200 bg-slate-50 p-4'>
                <span className='text-2xl font-bold text-slate-900'>30 €</span>
                <p className='text-sm text-slate-600'>TTC / mois</p>
              </div>
              <div className='rounded-xl border border-slate-200 bg-slate-50 p-4'>
                <span className='text-2xl font-bold text-slate-900'>300 €</span>
                <p className='text-sm text-slate-600'>TTC / an</p>
              </div>
            </div>
            <a
              href={MARKETING_URL}
              target='_blank'
              rel='noreferrer'
              className='mt-6 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700'
            >
              En savoir plus sur l’offre Site web personnalisable <ArrowUpRight className='h-4 w-4' />
            </a>
            <p className='mt-3 text-xs text-slate-500'>
              Accès activé manuellement par BoulesConnect pendant la phase pilote ; aucun paiement automatique ici.
            </p>
            {organizer && memberships.length > 1 && (
              <button
                type='button'
                className='mt-4 block text-sm font-semibold text-blue-700 underline'
                onClick={() => setSearchParams({})}
              >
                Changer d’organisation
              </button>
            )}
          </section>
        )}
      </div>
    </DashboardLayout>
  );
}
